import type { CatalogFieldState } from "@/lib/api/generated/casedb";
import type { JsonSchema } from "@jsonforms/core";
import type { CaseFieldValues } from "../model/caseReview";
import { hasReviewRecord } from "./caseReviewFieldState";
import { REVIEW_DECISIONS, type ReviewDecision } from "../constants/caseReviewDecisions";
import type { FieldKeyResolver } from "./caseReviewFieldState";

/** The part of any draft this operation touches; records and scalar sections both have it. */
export interface AcceptableDraft {
  values: CaseFieldValues;
  decisions: Record<string, ReviewDecision>;
  reverted: Set<string>;
}

/** The restrictions shared by a scalar form, a collection row and every review action. */
export interface ReviewFieldEligibility {
  sectionEditable: boolean;
  disabledFields: Record<string, string>;
  schema: JsonSchema | undefined;
}

/** Whether a field has a control the current actor is allowed to change. */
export function isFieldEditable(eligibility: ReviewFieldEligibility,
                                fieldId: string, key: string): boolean {
  if (!eligibility.sectionEditable || eligibility.disabledFields[fieldId] || eligibility.disabledFields[key]) return false;
  let node = eligibility.schema as (JsonSchema & { readOnly?: boolean }) | undefined;
  if (!node || node.readOnly === true) return false;
  for (const segment of key.split(".")) {
    const property = (node?.properties ?? {})[segment] as (JsonSchema & { readOnly?: boolean }) | undefined;
    if (!property) return false;
    if (property.readOnly === true) return false;
    node = property;
    if (node.type === "array") node = node.items as (JsonSchema & { readOnly?: boolean }) | undefined;
  }
  return true;
}

/**
 * Accept every AI value in one draft that nobody has decided on yet.
 *
 * <p>Pure, and it answers the same two questions a single Accept does, so the two cannot drift: a
 * field is acceptable when the response gave it a review row, and accepting it writes the typed
 * snapshot the reader published (`normalized_extracted_value`) rather than whatever the draft
 * happens to be showing. A field whose snapshot is unavailable — its extraction could not be used
 * as the field's type — is skipped: there is nothing to accept, and the reviewer enters it.
 *
 * <p>A reverted field is pending again by definition, so it is included and its reverted mark is
 * cleared. A field the reviewer has already accepted or corrected is left exactly as it is: this
 * is "accept what is outstanding", not "overwrite the review".
 */
/**
 * Whether this field is one Accept-all may decide.
 *
 * <p>The same question the per-field Accept asks: a review row exists, and nobody has decided yet.
 * Exported so the count the reviewer is shown and the action they press cannot disagree — a number
 * derived from anything else would promise decisions the action then does not make.
 */
export function isAcceptable(state: CatalogFieldState | undefined,
                             decision: ReviewDecision | undefined): boolean {
  return hasReviewRecord(state)
    && state?.normalized_extracted_value_available === true && !decision;
}

/** Whether the reader published a normalized value that is safe for mutation. */
export function normalizedValueAvailable(state: CatalogFieldState | undefined): boolean {
  return state?.normalized_extracted_value_available === true;
}

/** How many fields in one draft Accept-all would decide. */
export function countAcceptable(
  decisions: Record<string, ReviewDecision>,
  states: Record<string, CatalogFieldState>,
  keyOf: FieldKeyResolver,
  eligible: (fieldId: string, key: string) => boolean = () => true,
): number {
  return Object.entries(states).filter(([fieldId, state]) => {
    const key = keyOf(fieldId);
    return !!key && eligible(fieldId, key) && isAcceptable(state, decisions[key]);
  }).length;
}

export function acceptAllPending<T extends AcceptableDraft>(
  draft: T,
  states: Record<string, CatalogFieldState>,
  keyOf: FieldKeyResolver,
  eligible: (fieldId: string, key: string) => boolean = () => true,
): { draft: T; accepted: string[] } {
  const values: CaseFieldValues = { ...draft.values };
  const decisions = { ...draft.decisions };
  const reverted = new Set(draft.reverted);
  const accepted: string[] = [];

  for (const [fieldId, state] of Object.entries(states)) {
    const key = keyOf(fieldId);
    if (!key || !eligible(fieldId, key) || !isAcceptable(state, decisions[key])) continue;
    values[key] = state.normalized_extracted_value as CaseFieldValues[string];
    decisions[key] = REVIEW_DECISIONS.accepted;
    reverted.delete(key);
    accepted.push(key);
  }

  if (accepted.length === 0) return { draft, accepted };
  return { draft: { ...draft, values, decisions, reverted }, accepted };
}
